import type { AuthUser } from "@playbrain/auth/dashboardRoles"
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import {
  performLogoutAction,
  performMfaAction,
  performPasswordLoginAction,
  performSessionRefreshAction,
  requestPasswordResetAction,
} from "./actions"
import { getAuthConfig, isDashboardAuthBypassEnabled } from "./config"
import { readServerSession } from "./sessionApi"
import { clearTransaction } from "./transactions"
import type {
  AuthSession,
  DashboardAuthContextValue,
  DashboardMfaEnrollment,
  DashboardMfaMode,
  PasswordLoginResult,
} from "./types"
import { errorMessage } from "./utils"

const DashboardAuthContext = createContext<DashboardAuthContextValue | null>(null)

/**
 * `initialSession` is the session the server already resolved for this request.
 *
 * Without it the provider starts in a loading state and every consumer — the
 * gate, the nav, the permission checks — has to wait a client round-trip before
 * anything can render, which is what put a full-page skeleton in front of the
 * dashboard on every cold load. Supplied, the first paint already knows who you
 * are and the skeleton never appears; the background refresh below still runs,
 * so nothing about session freshness changes.
 */
export function DashboardAuthProvider({
  children,
  initialSession = null,
}: PropsWithChildren<{ initialSession?: AuthSession | null }>) {
  const [config] = useState(getAuthConfig)
  const [session, setSession] = useState<AuthSession | null>(initialSession)
  const [isLoading, setIsLoading] = useState(initialSession === null)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mfaEmail, setMfaEmail] = useState<string | null>(null)
  const [mfaMode, setMfaMode] = useState<DashboardMfaMode | null>(null)
  const [mfaEnrollment, setMfaEnrollment] = useState<DashboardMfaEnrollment | null>(null)
  const localDevUser = useMemo<AuthUser | null>(() => {
    if (config.isConfigured || !isDashboardAuthBypassEnabled()) return null
    return {
      sub: "local-dev",
      dashboardLanguage: "en",
      email: "local-dev@playbrain.com",
      name: "Local Dev",
      username: "Local Dev",
      user_metadata: { roles: ["admin"] },
    }
  }, [config.isConfigured])

  useEffect(() => {
    if (localDevUser) {
      setIsLoading(false)
      return
    }

    let active = true
    readServerSession()
      .then((restored) => {
        if (!active) return
        setSession(restored)
      })
      .catch(() => {
        if (!active) return
        setSession(null)
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [localDevUser])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const clearMfa = useCallback(() => {
    setMfaEmail(null)
    setMfaMode(null)
    setMfaEnrollment(null)
  }, [])

  const applySession = useCallback((nextSession: AuthSession | null) => {
    setSession(nextSession)
  }, [])

  const loginWithPassword = useCallback(
    async (email: string, password: string, rememberMe = false): Promise<PasswordLoginResult> => {
      setIsLoading(true)
      setError(null)
      try {
        const result = await performPasswordLoginAction(config, email, password, rememberMe)
        if (result.result === "authenticated") {
          applySession(result.session)
          clearMfa()
          return "authenticated"
        }

        setSession(null)
        setMfaEmail(email)
        setMfaMode(result.mode)
        setMfaEnrollment(result.enrollment ?? null)
        return "mfa_required"
      } catch (caughtError) {
        setSession(null)
        clearMfa()
        setError(errorMessage(caughtError, "Could not sign in."))
        throw caughtError
      } finally {
        setIsLoading(false)
      }
    },
    [applySession, clearMfa, config]
  )

  const completeMfa = useCallback(
    async (code: string) => {
      setIsLoading(true)
      setError(null)
      try {
        const nextSession = await performMfaAction(config, code)
        applySession(nextSession)
        clearMfa()
      } catch (caughtError) {
        setSession(null)
        setError(errorMessage(caughtError, "Could not verify MFA code."))
        throw caughtError
      } finally {
        setIsLoading(false)
      }
    },
    [applySession, clearMfa, config]
  )

  const requestPasswordReset = useCallback(
    async (email: string, locale?: "en" | "ja") => {
      await requestPasswordResetAction(config, email, locale)
    },
    [config]
  )

  const refreshSession = useCallback(async () => {
    if (localDevUser) return null
    const nextSession = await performSessionRefreshAction()
    applySession(nextSession)
    return nextSession
  }, [applySession, localDevUser])

  useEffect(() => {
    if (localDevUser || !session || typeof window === "undefined") return

    const refreshCurrentSession = () => {
      void refreshSession().catch(() => undefined)
    }
    const refreshVisibleSession = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return
      refreshCurrentSession()
    }

    window.addEventListener("focus", refreshCurrentSession)
    document.addEventListener("visibilitychange", refreshVisibleSession)
    return () => {
      window.removeEventListener("focus", refreshCurrentSession)
      document.removeEventListener("visibilitychange", refreshVisibleSession)
    }
  }, [localDevUser, refreshSession, session])

  const getAccessToken = useCallback(async () => {
    await refreshSession()
    return null
  }, [refreshSession])

  const logout = useCallback(() => {
    setIsLoggingOut(true)
    const logoutRequest = performLogoutAction().catch(() => null)
    setSession(null)
    setError(null)
    clearMfa()
    clearTransaction()
    void logoutRequest.finally(() => window.location.replace("/login"))
  }, [clearMfa])

  return (
    <DashboardAuthContext.Provider
      value={{
        isAuthenticated: Boolean(localDevUser || session),
        isLoading: isLoading || isLoggingOut,
        isLoggingOut,
        isConfigured: config.isConfigured,
        session: localDevUser ? null : session,
        user: localDevUser ?? session?.user ?? null,
        error,
        mfaRequired: Boolean(mfaMode),
        mfaEmail,
        mfaMode,
        mfaEnrollment,
        loginWithPassword,
        completeMfa,
        requestPasswordReset,
        logout,
        getAccessToken,
        refreshSession,
        clearMfa,
        clearError,
      }}
    >
      {children}
    </DashboardAuthContext.Provider>
  )
}

export function useDashboardAuth() {
  const context = useContext(DashboardAuthContext)
  if (!context) throw new Error("DashboardAuthProvider is missing.")
  return context
}
